/**
 * Game-style scrollbar for a scrollable container.
 *
 * The native bar is hidden and replaced by a thin track (drawn along the container's own edge) with a glitchy
 * square on it: the square rides down the track as the content scrolls, and can be dragged like a native thumb.
 * Everything else about scrolling stays native — wheel, touch, keyboard, momentum — because the container is still
 * the real scroller; this only mirrors its position and lets a drag/click drive it.
 *
 * The track is a SIBLING of the scroller (not a child), so re-rendering the scroller's innerHTML (the colony popups
 * do this on every data tick) never destroys it, and it is positioned over the scroller's own rows in the shared
 * parent, which therefore has to be a positioned box (it is made `relative` if it is `static`).
 */

const SQUARE_PX = 14;
/** Below this overflow (px) the content "fits" and no bar is shown — rounding noise must not flash a bar. */
const SCROLLABLE_SLACK_PX = 1;
const ACTIVE_MS = 700;

export interface GameScrollbar {
  /** Re-reads the scroller's size and position. Called automatically on scroll / resize / content change. */
  sync(): void;
  destroy(): void;
}

export function attachGameScrollbar(scroller: HTMLElement): GameScrollbar {
  const parent = scroller.parentElement;
  if (!parent) return { sync() {}, destroy() {} };

  const position = getComputedStyle(parent).position;
  if (!position || position === 'static') parent.style.position = 'relative';
  scroller.classList.add('gs-scroller');

  const track = document.createElement('div');
  track.className = 'gs-track';
  track.setAttribute('aria-hidden', 'true');
  track.hidden = true;
  const square = document.createElement('div');
  square.className = 'gs-square';
  track.appendChild(square);
  parent.appendChild(track);

  let disposed = false;
  let activeTimer: ReturnType<typeof setTimeout> | null = null;
  let drag: { startY: number; startScroll: number } | null = null;

  const maxScroll = () => Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  const squareSize = () => Math.min(SQUARE_PX, Math.max(0, scroller.clientHeight));

  function sync(): void {
    if (disposed) return;
    const scrollable = scroller.scrollHeight - scroller.clientHeight > SCROLLABLE_SLACK_PX;
    track.hidden = !scrollable;
    scroller.classList.toggle('gs-scrollable', scrollable);
    if (!scrollable) return;
    // Lay the track along the scroller's own box.
    track.style.top = `${scroller.offsetTop}px`;
    track.style.height = `${scroller.clientHeight}px`;
    const range = Math.max(0, scroller.clientHeight - squareSize());
    const ratio = maxScroll() > 0 ? Math.min(1, Math.max(0, scroller.scrollTop / maxScroll())) : 0;
    square.style.transform = `translateY(${Math.round(ratio * range)}px)`;
    square.classList.toggle('gs-square--end', ratio >= 0.999);
  }

  /** A short burst of the glitch whenever the position changes, so the square reads as live without flickering forever. */
  function flash(): void {
    track.classList.add('gs-active');
    if (activeTimer) clearTimeout(activeTimer);
    activeTimer = setTimeout(() => {
      activeTimer = null;
      if (!drag) track.classList.remove('gs-active');
    }, ACTIVE_MS);
  }

  function onScroll(): void {
    sync();
    flash();
  }

  // ── Dragging the square: pointer travel along the track maps onto the full scroll range. ──
  function onSquareDown(e: PointerEvent): void {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    drag = { startY: e.clientY, startScroll: scroller.scrollTop };
    track.classList.add('gs-active', 'gs-dragging');
    square.setPointerCapture?.(e.pointerId);
  }
  function onSquareMove(e: PointerEvent): void {
    if (!drag) return;
    const range = scroller.clientHeight - squareSize();
    if (range <= 0) return;
    scroller.scrollTop = drag.startScroll + ((e.clientY - drag.startY) / range) * maxScroll();
  }
  function onSquareUp(e: PointerEvent): void {
    if (!drag) return;
    drag = null;
    track.classList.remove('gs-dragging');
    square.releasePointerCapture?.(e.pointerId);
    flash();
  }

  // ── Clicking the empty track pages toward the click, like a native bar. ──
  function onTrackDown(e: PointerEvent): void {
    if (e.target !== track) return;
    e.preventDefault();
    const rect = track.getBoundingClientRect();
    const squareMid = rect.top + parseFloat(square.style.transform.replace(/[^\d.-]/g, '') || '0') + squareSize() / 2;
    scroller.scrollTop += (e.clientY < squareMid ? -1 : 1) * scroller.clientHeight * 0.9;
  }

  // The track sits outside the scroller, so a wheel over it would otherwise do nothing.
  function onTrackWheel(e: WheelEvent): void {
    e.preventDefault();
    scroller.scrollTop += e.deltaY;
  }

  scroller.addEventListener('scroll', onScroll, { passive: true });
  square.addEventListener('pointerdown', onSquareDown);
  square.addEventListener('pointermove', onSquareMove);
  square.addEventListener('pointerup', onSquareUp);
  square.addEventListener('pointercancel', onSquareUp);
  track.addEventListener('pointerdown', onTrackDown);
  track.addEventListener('wheel', onTrackWheel, { passive: false });

  // Content is swapped via innerHTML and images load late, so watch the scroller's size AND its children.
  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => sync());
    resizeObserver.observe(scroller);
  }
  if (typeof MutationObserver !== 'undefined') {
    mutationObserver = new MutationObserver(() => sync());
    mutationObserver.observe(scroller, { childList: true, subtree: true, characterData: true });
  }
  scroller.addEventListener('load', sync, true); // late images change the height without a mutation
  window.addEventListener('resize', sync);
  sync();

  return {
    sync,
    destroy(): void {
      if (disposed) return;
      disposed = true;
      if (activeTimer) clearTimeout(activeTimer);
      scroller.removeEventListener('scroll', onScroll);
      scroller.removeEventListener('load', sync, true);
      window.removeEventListener('resize', sync);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      scroller.classList.remove('gs-scroller', 'gs-scrollable');
      track.remove();
    },
  };
}
